import instance from "../core/instance";
import { StorageBox } from "../core/storage";
import { REGISTRATION_PLATFORM_MCARE } from "../constants/registrationPlatform";

const DataProvider = {
  signinWithPhoneNumber: async (phoneNumber: string, recaptchaToken: string) => {
    return await instance.post("/auth/signin-phone", { phoneNumber, recaptchaToken });
  },
  verifyPhoneNumberAndSignin: async (payload: {sessionInfo: string, otpCode: string}) => {
    return await instance.post("/auth/signin-verify", payload);
  },
  signupWithPhoneNumber: async (phoneNumber: string, recaptchaToken: string) => {
    return await instance.post("/auth/signup-phone", {
      phoneNumber,
      recaptchaToken,
      registrationPlatform: REGISTRATION_PLATFORM_MCARE,
    });
  },
  verifyPhoneNumberAndSignup: async (payload: {
    sessionInfo: string;
    otpCode: string;
    fullname: string;
    phoneNumber: string;
  }) => {
    return await instance.post("/auth/signup-verify", {
      ...payload,
      registrationPlatform: REGISTRATION_PLATFORM_MCARE,
    });
  },
  forgotpassword: async (payload: any) => {
    return await instance.post("/user-forgot-password", payload);
  },
  verifypasswordotp: async (
    emailOrPhone: { email?: string; phoneNumber?: string },
    otpCode: string,
  ) => {
    const params = new URLSearchParams({ otpCode: otpCode.trim() });
    const email = emailOrPhone.email?.trim().toLowerCase();
    const phoneNumber = emailOrPhone.phoneNumber?.trim();
    if (email) params.set("email", email);
    if (phoneNumber) params.set("phoneNumber", phoneNumber);
    if (!email && !phoneNumber) {
      throw new Error("Email or phone number is required to verify OTP.");
    }
    return await instance.get(`/user-forgot-password/verify?${params.toString()}`);
  },
  resetPassword: async (payload: any) => {
    return await instance.post("/user-forgot-password/update", payload);
  },
  getData: async (page: number, size: number) => {
    return await instance.get(
      `/prescriptions/query?pageNumber=${page}&pageSize=${size}`
    );
  },
  getOrder: async (id: string) => {
    return await instance.get(`/prescriptions/${id}`);
  },
  getPartners: (filters = null) => {
    return instance.get(`/partners?${filters}`);
  },
  setToken: async (token: string) => {
    StorageBox.setToken(token);
    instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  },
  setRefreshToken: async (refreshToken: string) => {
    StorageBox.setRefreshToken(refreshToken);
  },
  clearTokens: () => {
    StorageBox.clearStorage();
  },
  getToken: async () => {
    return StorageBox.getToken();
  },
  cancelOrder: (data: any) => {
    return instance.patch(`/prescriptions/${data.id}/cancel`, {
      reason: data.reason,
    });
  },
  addInsurance: (data: any) => {
    return instance.post("/me/insurance", data);
  },
  getInsurance: async () => {
    return await instance.get("/me/insurance");
  },
  deleteInsurance: (id: string) => {
    return instance.delete(`/me/insurance/${id}`);
  },
  getDelivery: () => {
    return instance.get("/me/address");
  },
  deleteDelivery: async (id: number) => {
    return await instance.delete(`/me/address/${id}`);
  },
  addAddress: (data: any) => {
    return instance.post("/me/address", data);
  },
  requestS3: async (data: any) => {
    return await instance.post("/upload", data);
  },
  cloudUpload: async (data: any) => {
    return await instance.post("/upload/img", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  submitPrescription: (data: any) => {
    return instance.post("/prescriptions", data);
  },
  deliveryMethods: () => {
    return instance.get("/delivery-methods");
  },
  doctorPrescription: (data: any) => {
    return instance.post("/doctors/prescriptions", data);
  },
  getProfile: async () => {
    return await instance.get("/auth/me");
  },

  updateProfile: (data: any) => {
    return instance.patch("/auth/me", data);
  },
  registerToken: (payload: any) => {
    return instance.post("/notifications", payload);
  },
  getNotifications: (filter = false) => {
    return instance.get("/notifications/messages?readAll=" + filter);
  },
  markNotificationAsRead: (id: string) => {
    return instance.patch(`/notifications/messages/${id}/read`);
  },
  submitConsultation: (payload: any) => {
    return instance.post("/consultations", payload);
  },
  getConsultations: () => {
    return instance.get("/consultations");
  },
  getConsultationPrices: () => {
    return instance.get("/consultations-prices");
  },
  getStaffData: async () => {
    return await instance.get("/partner-staff");
  },
  addStaffData: (payload: any) => {
    return instance.post("/partner-staff", payload);
  },
  updateStaffData: (id: string, payload: any) => {
    return instance.patch(`/partner-staff/${id}`, payload);
  },
  verifystaffData: (email: string) => {
    return instance.patch(`/partner-staff/verify/${email}`);
  },
  sendVerificationEmail: (payload: any) => {
    return instance.post("/verify", payload);
  },
  verifyStaffEmail: (id: string) => {
    return instance.get(`/verify/${id}`);
  },
  submitDiagnostic: async (payload: any) => {
    return await instance.post("/diagnostics", payload);
  },
  getDiagnostics: async (page: number, size: number) => {
    return await instance.get(
      `/diagnostics/query?pageNumber=${page}&pageSize=${size}`
    );
  },
  getDiagnosticOrder: async (id: string) => {
    return await instance.get(`/diagnostics/${id}`);
  },
  getDiagnosticPrices: async () => {
    return await instance.get("/diagnostic-prices");
  },

  getMedicalRecords: async (page: number, size: number) => {
    return await instance.get(
      `/medical-records/query?pageNumber=${page}&pageSize=${size}`
    );
  },
  getDoctors: async (page: number, size: number) => {
    return await instance.get(
      `/doctors/query?pageNumber=${page}&pageSize=${size}`
    );
  },

  getSingleDoctor: async (doctorId: number) => {
    return await instance.get(`/doctors/${doctorId}`);
  },

  matchDoctor: async (payload: any) => {
    return await instance.post(`/doctors/match`, payload);
  },

  doctorMatching: async (payload: any) => {
    return await instance.post("/doctors/match", payload);
  },

  getDoctorSchedules: async (doctorId: number) => {
    return await instance.get(`/schedule/${doctorId}`);
  },

  getAvailableSchedule: async () => {
    return await instance.get("/schedule");
  },

  createAppointment: async (payload: any) => {
    return await instance.post("/appointments", payload);
  },
  getUpcomingAppointment: async () => {
    return await instance.get("/appointments");
  },
  getAllAppointment: async (page: number, size: number) => {
    return await instance.get(
      `/appointments/query?pageNumber=${page}&pageSize=${size}`
    );
  },
  getSingleAppointment: async (apptId: string) => {
    return await instance.get(`/appointments/${apptId}`);
  },
  cancelAppointment: async (apptId: string | number, cancelReason: string) => {
    return await instance.patch(`/appointments/${apptId}/cancel`, {
      cancelReason,
    });
  },
  rescheduleAppointment: async (
    apptId: string | number,
    payload: {
      scheduleId: number;
      timeFrameId: number;
      rescheduleReason?: string;
    }
  ) => {
    return await instance.patch(`/appointments/${apptId}/reschedule`, payload);
  },

  getLabCeneters: async (page?: number, size?: number) => {
    return await instance.get(
      `/labs/query?pageNumber=${page}&pageSize=${size}`
    );
  },

  payDiagnosticOrder: async (id: number, payload: any) => {
    return await instance.post(`/payments/diagnostic/${id}`, payload);
  },

  sendOTp: async (otp: number, reference: any) => {
    return await instance.get(`/payments/send-otp/${otp}/${reference}`);
  },

  //macare
  getAllPackages: async () => {
    return await instance.get("/mcare");
  },
  unsubscribe: async (payload: any) => {
    return await instance.post("/subscriptions/unsubscribe", payload);
  },
  requestSubscription: async (payload: any) => {
    return await instance.post("/subscriptions/request", payload);
  },

  /** Validate coupon code (public). Returns { valid, message, serviceType, packageType, durationMonths, benefits? }. */
  validateCoupon: async (code: string) => {
    const { data } = await instance.get(
      "/customer-engagement/coupons/validate",
      { params: { code: code?.trim() || "" } },
    );
    return data;
  },
  changeSubscription: async (payload: any) => {
    return await instance.post("/subscriptions/change", payload);
  },
  renewSubscription: async (payload: any) => {
    return await instance.post("/subscriptions/renewal", payload);
  },
  getUserSubscription: async () => {
    return await instance.get("/subscriptions/me");
  },
  nextOfKin: async (payload: any, id: number) => {
    return await instance.post(`/subscriptions/${id}/next-of-kin`, payload);
  },
  updateNextOfKin: async (payload: any, id: number) => {
    return await instance.patch(`/subscriptions/next-of-kin/${id}`, payload);
  },
  activateWithYelloCare: async (payload: any) => {
    return await instance.post(
      "/subscriptions/activate-with-yello-care",
      payload
    );
  },

  //claims
  getClaims: async () => {
    return await instance.get("/claims/user");
  },
  createDeathClaim: async (payload: any) => {
    return await instance.post("/claims/death", payload);
  },
  createHospitalizedClaim: async (payload: any) => {
    return await instance.post("/claims/hospitalized", payload);
  },

  //reports
  getReports: async (userId: number) => {
    return await instance.get(`/reports/${userId}`);
  },
  // getMedicalRecords: async (userId: number) => {
  //   return await instance.get(`/reports/${userId}`);
  // },

  getMeeting: async () => {
    return await instance.get("/chime");
  },
  getDailyMeeting: async (appointmentId: number) => {
    return await instance.get(`/daily/room/${appointmentId}`);
  },
  createNewTracking: async (payload: any) => {
    return await instance.post("/tracking", payload);
  },
  updateTracking: async (payload: any) => {
    return await instance.patch("/tracking", payload);
  },

  //Open AI - Health Chat Service
  requestSymptomExplanation: async (payload: {
    message: string;
    previousMessages?: { role: "user" | "assistant"; content: string }[];
  }) => {
    return await instance.post("/health-bot/chat", payload);
  },

  // Kobikuul AI (ai-integration v2) – authenticated
  kobikuulV2: async (payload: { message: string; userId?: string } | FormData) => {
    return await instance.post(
      "/ai-integration/v2/chat",
      payload,
      payload instanceof FormData ? {} : {}
    );
  },
  getKobikuulChatHistory: async (pageNumber = 1, pageSize = 20) => {
    return await instance.get<import("../types/chat").ChatHistoryResponse>(
      `/ai-integration/v2/chat/history?pageNumber=${pageNumber}&pageSize=${pageSize}`
    );
  },
  getKobikuulUsage: async () => {
    return await instance.get("/ai-integration/v2/chat/usage");
  },
  acceptPrivacyConsent: async () => {
    return await instance.patch("/auth/me", {
      privacyConsentAccepted: true,
    });
  },

  // Kobikuul AI (ai-integration v2) – anonymous guests / MTN campaign
  // (?source=pulse|prestige routes to the monthly campaign budget).
  // withCredentials: the guest/campaign identity rides on an httpOnly
  // cookie the API mints on the first message.
  kobikuulV2Anonymous: async (payload: {
    message: string;
    sessionId?: string;
    previousMessages?: { role: "user" | "system"; content: string }[];
    source?: string;
    campaign?: string;
  }) => {
    return await instance.post<{
      reply: string;
      sessionId: string;
      remaining: number;
      limitReached?: boolean;
      trackingId?: string;
      onboardingPrompt?: import("../components/kobikuul/OnboardingCard").CampaignOnboardingPrompt;
      upgradePrompt?: object;
    }>("/ai-integration/v2/chat/anonymous", payload, {
      withCredentials: true,
    });
  },
  getKobikuulAnonymousLimits: async () => {
    return await instance.get("/ai-integration/v2/chat/anonymous/limits", {
      withCredentials: true,
    });
  },
  // Dev/staging only (API rejects it in production): clears the daily guest
  // counters and THIS browser's campaign monthly budget (cookie identity).
  resetKobikuulGuestLimits: async () => {
    return await instance.post(
      "/ai-integration/v2/chat/anonymous/reset",
      {},
      { withCredentials: true },
    );
  },
  // Landing beacon — fired on campaign page load, before any message.
  // Records the arrival in the attribution funnel (tap-and-bounce visitors
  // become visible in drop-off reporting) and returns the monthly budget so
  // the counter shows immediately.
  kobikuulCampaignLanding: async (payload: {
    source: string;
    campaign?: string;
  }) => {
    return await instance.post<{
      trackingId: string;
      remaining: number;
      limit: number;
      limitReached: boolean;
      /** Present when a returning guest's budget is already spent — the page
       *  re-renders the signup card immediately. */
      onboardingPrompt?: import("../components/kobikuul/OnboardingCard").CampaignOnboardingPrompt;
    }>("/ai-integration/v2/chat/anonymous/campaign-landing", payload, {
      withCredentials: true,
    });
  },
};

export default DataProvider;
